/**
 * `ApiError` — the value our axios response interceptor rejects with when the
 * server answered with a non-2xx status.
 *
 * Historically the interceptor rejected with the raw axios `error.response`
 * object (`{ config, data, headers, request, status }`). Call sites rely on
 * that shape (`err.status`, `err.data.message`, …), so `ApiError` keeps every
 * one of those fields as own properties. What it adds is that the rejection
 * is now a real `Error`:
 *
 *   - unhandled rejections reach PostHog / Sentry with a type, a message and a
 *     stack instead of "Object captured as exception with keys: config, data,
 *     headers, request, status" (PostHog issue 01a03d9c-3476-7400-9877-471fe5f69e46);
 *   - `err.message` is a human-readable string (the server's message when it
 *     sent one), so generic `err.message || fallback` code paths work.
 */

const MAX_SERVER_MESSAGE_LENGTH = 200;

/**
 * Pull a short, human-readable message out of a server error payload.
 * Accepts the shapes our NestJS / Express backends emit:
 *   `{ message: "..." }`, `{ message: ["...", "..."] }` (class-validator),
 *   `{ error: "..." }`, `{ msg: "..." }`.
 * Anything else (HTML error pages, blobs, long strings) yields `undefined`.
 *
 * @param {unknown} data
 * @returns {string | undefined}
 */
export function extractServerMessage(data) {
  if (!data || typeof data !== "object") return undefined;
  const candidates = [data.message, data.error, data.msg];
  for (const candidate of candidates) {
    const text = Array.isArray(candidate)
      ? candidate.filter((part) => typeof part === "string").join(", ")
      : candidate;
    if (
      typeof text === "string" &&
      text.trim() &&
      text.length <= MAX_SERVER_MESSAGE_LENGTH
    ) {
      return text.trim();
    }
  }
  return undefined;
}

/**
 * Strip the origin and query string from a request URL so messages stay
 * stable (and free of tokens) across environments.
 *
 * @param {string | undefined} url
 * @returns {string | undefined}
 */
export function describeRequestPath(url) {
  if (!url || typeof url !== "string") return undefined;
  try {
    return new URL(url, "http://placeholder.local").pathname;
  } catch (_error) {
    return url.split("?")[0];
  }
}

export class ApiError extends Error {
  /**
   * @param {object} response - an axios `error.response`
   */
  constructor(response) {
    const { status, statusText, data, headers, config, request } =
      response || {};
    const serverMessage = extractServerMessage(data);
    super(
      serverMessage ||
        (status
          ? `Request failed with status ${status}`
          : "Request failed without a status code")
    );
    this.name = "ApiError";

    // Legacy shape — keep every field callers already read.
    this.status = status;
    this.statusText = statusText;
    this.data = data;
    this.headers = headers;
    this.config = config;
    this.request = request;

    // Extras for diagnostics.
    this.response = response;
    this.method = config?.method ? String(config.method).toUpperCase() : undefined;
    this.url = describeRequestPath(config?.url);
  }
}

/**
 * True when `value` is an `ApiError` (works across module copies).
 * @param {unknown} value
 */
export function isApiError(value) {
  return value instanceof ApiError || value?.name === "ApiError";
}

/**
 * Normalise an axios failure into the value the interceptor should reject
 * with: an `ApiError` when the server answered, otherwise the original error
 * untouched (network failures, timeouts and cancellations are already
 * `Error`s and callers check them with `axios.isCancel`).
 *
 * @param {unknown} error
 * @returns {unknown}
 */
export function toRejection(error) {
  if (isApiError(error)) return error;
  if (error && typeof error === "object" && error.response) {
    return new ApiError(error.response);
  }
  return error;
}
